import { identity } from "deso-protocol";

import type { PostViewRequest } from "@/backend/focus";
import { sendPostViews } from "@/backend/focus";
import {
  hasRecentlySentPostView,
  recordSentPostViews,
} from "@/utils/post-view-cache";

const POST_VIEW_BATCH_WINDOW_MS = 3_000;
const POST_VIEW_BATCH_MAX_SIZE = 50;

type QueuedPostView = {
  jwtPromise: Promise<string>;
  postHash: string;
  publicKey: string;
};

type QueuePostViewEntry = {
  postHash: string;
  publicKey: string;
};

const queuedPostViews = new Map<string, QueuedPostView>();
const jwtPromisesByPublicKey = new Map<string, Promise<string>>();

let flushTimeout: number | null = null;
let flushing = false;
let lifecycleListenersInstalled = false;

const getPostViewQueueKey = ({ postHash, publicKey }: QueuePostViewEntry) =>
  `${publicKey}:${postHash}`;

const getPostViewJwtPromise = (publicKey: string) => {
  let jwtPromise = jwtPromisesByPublicKey.get(publicKey);

  if (!jwtPromise) {
    jwtPromise = identity.jwt().catch(() => "");
    jwtPromisesByPublicKey.set(publicKey, jwtPromise);
  }

  return jwtPromise;
};

const clearFlushTimeout = () => {
  if (!flushTimeout) {
    return;
  }

  clearTimeout(flushTimeout);
  flushTimeout = null;
};

const schedulePostViewFlush = () => {
  if (typeof window === "undefined" || flushTimeout || flushing) {
    return;
  }

  flushTimeout = window.setTimeout(() => {
    flushTimeout = null;
    void flushPostViewQueue({ keepalive: true });
  }, POST_VIEW_BATCH_WINDOW_MS);
};

const hasPendingPostViewsForPublicKey = (publicKey: string) => {
  for (const entry of queuedPostViews.values()) {
    if (entry.publicKey === publicKey) {
      return true;
    }
  }

  return false;
};

const cleanupUnusedJwtPromises = () => {
  for (const publicKey of jwtPromisesByPublicKey.keys()) {
    if (!hasPendingPostViewsForPublicKey(publicKey)) {
      jwtPromisesByPublicKey.delete(publicKey);
    }
  }
};

const installPostViewQueueLifecycleListeners = () => {
  if (
    lifecycleListenersInstalled ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  window.addEventListener("pagehide", () => {
    void flushPostViewQueue({ keepalive: true });
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushPostViewQueue({ keepalive: true });
    }
  });

  lifecycleListenersInstalled = true;
};

const takePostViewBatch = () => {
  const batch = Array.from(queuedPostViews.values()).slice(
    0,
    POST_VIEW_BATCH_MAX_SIZE,
  );

  for (const entry of batch) {
    queuedPostViews.delete(getPostViewQueueKey(entry));
  }

  return batch;
};

const groupPostViewsByPublicKey = (batch: QueuedPostView[]) => {
  const groupedPostViews = new Map<string, QueuedPostView[]>();

  for (const entry of batch) {
    const entries = groupedPostViews.get(entry.publicKey) ?? [];
    entries.push(entry);
    groupedPostViews.set(entry.publicKey, entries);
  }

  return groupedPostViews;
};

const sendQueuedPostViewGroup = async (
  publicKey: string,
  entries: QueuedPostView[],
  keepalive: boolean,
) => {
  const jwt = await entries[0]!.jwtPromise;
  const postViews: PostViewRequest[] = entries.map((entry) => ({
    PostHash: entry.postHash,
    PublicKey: entry.publicKey,
  }));

  recordSentPostViews(entries);

  try {
    await sendPostViews(postViews, publicKey, {
      keepalive,
      jwt,
    });
  } catch (error) {
    console.error("Error sending post views", error);
  }
};

export const flushPostViewQueue = async ({
  keepalive = true,
}: {
  keepalive?: boolean;
} = {}) => {
  if (flushing) {
    return;
  }

  clearFlushTimeout();
  flushing = true;

  try {
    while (queuedPostViews.size > 0) {
      const batch = takePostViewBatch();
      const groupedPostViews = groupPostViewsByPublicKey(batch);

      await Promise.all(
        Array.from(groupedPostViews.entries()).map(([publicKey, entries]) =>
          sendQueuedPostViewGroup(publicKey, entries, keepalive),
        ),
      );
    }
  } finally {
    flushing = false;
    cleanupUnusedJwtPromises();

    if (queuedPostViews.size > 0) {
      schedulePostViewFlush();
    }
  }
};

export const queuePostView = (entry: QueuePostViewEntry) => {
  if (!entry.publicKey || !entry.postHash) {
    return false;
  }

  if (hasRecentlySentPostView(entry)) {
    return true;
  }

  const queueKey = getPostViewQueueKey(entry);

  if (queuedPostViews.has(queueKey)) {
    return true;
  }

  installPostViewQueueLifecycleListeners();
  queuedPostViews.set(queueKey, {
    ...entry,
    jwtPromise: getPostViewJwtPromise(entry.publicKey),
  });
  schedulePostViewFlush();

  return true;
};

export const resetPostViewQueueForTests = () => {
  clearFlushTimeout();
  queuedPostViews.clear();
  jwtPromisesByPublicKey.clear();
  flushing = false;
};
